import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { LoggerService } from "../../ancestors/logger.service";
import { EnvironmentLoaderService } from "../../ancestors/env-config.service";
import { AuthnApiService } from "./api/authn-api.service";
import { TokenStorageService } from "./token-storage.service";
import { TokenManagerService } from "./token-manager.service";
import { UrlCollection } from "../constants/url-collection";
import { AdminImpersonateComponent } from "../pages/admin/admin-impersonate/admin-impersonate.component";
import { AdminMonitoringDetailsComponent } from "../pages/admin/monitoring-details/monitoring-details.component";
import { NotificationStateService } from "./state/notification-state.service";

/**
 * Servizio per gestire il logout completo dell'utente
 * Coordina tutte le operazioni necessarie per un logout sicuro
 */
@Injectable({ providedIn: "root" })
export class LogoutService {
  private logger = inject(LoggerService);

  private router = inject(Router);

  private envConfig = inject(EnvironmentLoaderService);

  private authnService = inject(AuthnApiService);

  private tokenStorage = inject(TokenStorageService);

  private tokenManager = inject(TokenManagerService);

  private notificationState = inject(NotificationStateService);

  constructor() {
    this.logger.setCaller(this.constructor.name);
  }

  /**
   * Effettua il logout completo dell'utente
   * - Chiama l'API di logout
   * - Rimuove il token dallo storage locale
   * - Pulisce la sessione SSO se presente
   * - Reindirizza all'Identity Provider per il logout SSO (se applicabile)
   * - Oppure reindirizza alla pagina di login locale
   * @returns Promise di completamento
   */
  async performLogout(): Promise<void> {
    this.logger.info("Starting performLogout()");

    // Determina COME l'utente ha effettuato l'accesso in questa sessione: e il metodo di login
    // (non il loginType statico dell'ambiente) a decidere la destinazione del logout. In ambienti
    // con loginType="sso" e comunque possibile il login locale (es. backoffice), che al logout
    // deve tornare alla pagina di login locale.
    const env = this.envConfig.getEnvConfig();
    const loginMethod = this.tokenStorage.getLoginMethod();
    // Fallback per sessioni precedenti al deploy di questo codice (marcatore assente):
    // si ricade sul loginType dell'ambiente, come da comportamento storico.
    const isSsoLogin = loginMethod
      ? loginMethod === "sso"
      : env.loginType === "sso";

    try {
      // 1. Chiave del token nello storage (coerente col salvataggio: vedi TokenManagerService)
      const tokenKey = env.loginType === "local"
        ? env.localTokenKey
        : env.ssoTokenKey;

      this.logger.info(`Login method: ${loginMethod ?? "unknown"} (resolved isSsoLogin: ${isSsoLogin})`);

      // 1b. Per SSO SAML: recupera l'URL di Single Logout PRIMA di invalidare la sessione
      // (la chiamata e autenticata tramite il cookie di sessione: dopo il logout sul backend
      // non sarebbe piu possibile). Best-effort: se fallisce si procede col solo logout locale.
      let samlLogoutUrl: string | null = null;
      if (isSsoLogin && (env.ssoType ?? "oauth") === "saml") {
        try {
          samlLogoutUrl = (await this.authnService.getSamlLogoutUrl()).logoutUrl;
          this.logger.info(`SAML SLO URL ${samlLogoutUrl
            ? "retrieved"
            : "not available"}`);
        } catch (sloError) {
          this.logger.warn(`Unable to retrieve SAML logout URL, continuing with local logout: ${String(sloError)}`);
        }
      }

      // 2. Chiama l'API di logout sul backend
      try {
        await this.authnService.logout(true);
        // disconnect SSE
        this.notificationState.disconnectSSE();
        this.logger.info("Logout API call completed");
      } catch (apiError) {
        // Se l'API fallisce, continuiamo comunque con la pulizia locale
        this.logger.warn(`Errore nella chiamata API logout, continuo con pulizia locale: ${String(apiError)}`);
      }

      // 3. Rimuove il token dallo storage locale
      this.tokenStorage.deleteToken(tokenKey);
      this.logger.info("Token removed from storage");

      // 4. Pulisce i dati di sessione locale
      this.clearSessionData();

      // 5. Pulisce il marcatore del metodo di login
      this.tokenStorage.clearLoginMethod();
      this.logger.info("Login method marker removed");

      // 6. Redirect coerente col metodo di accesso usato nella sessione
      if (isSsoLogin) {
        // SSO: logout sull'Identity Provider (SLO SAML o logout OAuth)
        await this.performSsoLogout(samlLogoutUrl);
      } else {
        // 7. Login locale: reindirizza alla pagina di login locale
        this.logger.info(`Redirecting to: ${UrlCollection.localAuthentication}`);
        await this.router.navigate([UrlCollection.localAuthentication]);

        // 8. Ricarica la pagina per pulire completamente lo stato dell'applicazione
        window.location.reload();
      }
    } catch (error) {
      this.logger.error("Errore durante il logout", String(error));

      // In caso di errore, forza comunque la pulizia e il redirect coerente col metodo
      this.forceLogout(isSsoLogin);
    }
  }

  /**
   * Effettua il logout sull'Identity Provider SSO, in base al tipo di SSO configurato (ssoType)
   * @param samlLogoutUrl URL di Single Logout SAML recuperato PRIMA dell'invalidazione della
   *                      sessione (solo per ssoType "saml"; null se SLO non disponibile)
   * @private
   */
  private async performSsoLogout(samlLogoutUrl: string | null = null): Promise<void> {
    const ssoType = this.envConfig.getEnvConfig()?.ssoType ?? "oauth";

    if (ssoType === "saml") {
      if (samlLogoutUrl) {
        // Single Logout SP-initiated: si reindirizza all'IdP con la LogoutRequest firmata.
        // L'IdP terminera la propria sessione e inviera la LogoutResponse all'endpoint
        // /saml/slo del backend, che reindirizzera alla pagina post-logout (/#/sso).
        this.logger.info("SAML SSO logout: redirecting to IdP Single Logout");
        window.location.href = samlLogoutUrl;
        return;
      }

      // SLO non disponibile (IdP senza SingleLogoutService o sessione SAML scaduta):
      // la sessione applicativa e comunque invalidata; quella sull'IdP resta viva e il
      // login successivo avverra in modo automatico/silenzioso.
      this.logger.info("SAML SSO logout: SLO not available, redirecting to SSO login");
      window.location.href = `/#/${UrlCollection.sso}`;
      return;
    }

    try {
      // Costruisce l'URL di redirect post-logout
      // Dopo il logout SSO, l'utente verrà reindirizzato alla pagina SSO dell'app
      // const baseUrl = window.location.origin;
      // const postLogoutRedirectUri = `${baseUrl}/#/${UrlCollection.sso}`;
      // Verrà utilizzata la pagina di logout standard dell'Identity Provider, senza riportare l'utente all'applicazione.
      const postLogoutRedirectUri: string | undefined = undefined;

      this.logger.info(`Requesting SSO logout URL with redirect to: ${postLogoutRedirectUri}`);

      // Ottiene l'URL di logout dall'Identity Provider
      const response = await this.authnService.getOAuthLogoutUrl(postLogoutRedirectUri);

      this.logger.info(`Redirecting to SSO logout URL: ${response.logoutUrl}`);

      // Reindirizza all'Identity Provider per il logout
      window.location.href = response.logoutUrl;
    } catch (error) {
      this.logger.error(`Error during SSO logout: ${String(error)}`);

      // Se il logout SSO fallisce, effettua comunque un logout locale
      this.logger.warn("SSO logout failed, falling back to local redirect");
      window.location.href = `/#/${UrlCollection.sso}`;
    }
  }

  /**
   * Pulisce tutti i dati di sessione locale
   * @private
   */
  private clearSessionData(): void {
    sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.SEARCH_VALUE);
    sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.PAGE);
    sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.PAGE_SIZE);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SEARCHED_TEXT);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE_SIZE);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_FEEDBACK_360_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SELF_ASSESSMENT_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_EVALUATION_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_DELIVERY_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_FEEDBACK_360_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_SELF_ASSESSMENT_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_EVALUATION_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_DELIVERY_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SORT_OPTION);
    this.logger.info("Session data cleared");
  }

  /**
   * Logout forzato in caso di errori
   * Pulisce tutto localmente e reindirizza coerentemente col metodo di accesso usato
   * @param isSsoLogin true se la sessione era un login SSO (decide la pagina di destinazione)
   * @private
   */
  private forceLogout(isSsoLogin: boolean): void {
    this.logger.warn("Esecuzione logout forzato");

    try {
      const env = this.envConfig.getEnvConfig();

      // Rimuove tutti i token possibili
      this.tokenStorage.deleteToken(env.localTokenKey);
      this.tokenStorage.deleteToken(env.ssoTokenKey);

      // Pulisce il marcatore del metodo di login
      this.tokenStorage.clearLoginMethod();

      // Reindirizza e ricarica in base al metodo di accesso usato
      const redirectUrl = isSsoLogin
        ? UrlCollection.sso
        : UrlCollection.localAuthentication;
      window.location.href = `/#/${redirectUrl}`;
    } catch (forceError) {
      this.logger.error("Errore anche nel logout forzato", String(forceError));
      // Come ultima risorsa, vai alla home e ricarica
      window.location.href = "/";
    }
  }

  /**
   * Verifica se l'utente è attualmente autenticato
   * @returns true se l'utente ha un token valido
   */
  isAuthenticated(): boolean {
    return this.tokenManager.isAuthenticated();
  }
}
